import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import dayjs from 'dayjs';
import EditPaymentStatusModal from './EditPaymentStatusModal';
import B2BUtilsService from '../../../services/B2BUtilsService';
import { formatMoney } from '../../../utils/currency';

const ViewBillingStatementModal = ({ open, onClose, statement }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAllItems, setShowAllItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModifyBalanceModalOpen, setIsModifyBalanceModalOpen] = useState(false);

  // Log del statement que llega al modal
  React.useEffect(() => {
    if (statement) {
      console.log('🔍 ViewBillingStatementModal - Statement recibido:', statement);
      console.log('📊 ViewBillingStatementModal - Tipo de statement:', typeof statement);
      console.log('📊 ViewBillingStatementModal - Keys del statement:', Object.keys(statement || {}));
      console.log('📊 ViewBillingStatementModal - statement.orders:', statement.orders);
      console.log('📊 ViewBillingStatementModal - Tipo de orders:', typeof statement.orders);
      console.log('📊 ViewBillingStatementModal - Cantidad de orders:', statement.orders?.length);
      
      if (statement.orders && statement.orders.length > 0) {
        console.log('📊 ViewBillingStatementModal - Primer order:', statement.orders[0]);
        console.log('📊 ViewBillingStatementModal - Keys del primer order:', Object.keys(statement.orders[0] || {}));
      }
    }
  }, [statement]);

  if (!statement) return null;

  const getCompanyStatusColor = (status) => {
    return B2BUtilsService.getCompanyStatusColor(status);
  };

  const getCompanyStatusText = (status) => {
    return B2BUtilsService.getCompanyStatusText(status, t);
  };

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'cash': return t('cash');
      case 'transfer': return t('bank_transfer');
      case 'check': return t('check');
      case 'card': return t('credit_card');
      default: return method;
    }
  };

  const getTotalPaidForItem = (payments) => {
    return payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const handleModifyBalance = (item) => {
    setSelectedItem(item);
    setIsModifyBalanceModalOpen(true);
  };

  const handleUpdateBalance = (updatedBalanceData) => {
    // Aquí se actualizaría el saldo del item
    console.log('Updating balance for item:', selectedItem.id, updatedBalanceData);
    setIsModifyBalanceModalOpen(false);
  };


  // Usar los pedidos del statement o array vacío si no hay pedidos
  const items = statement?.orders || [];
  
  // Mostrar siempre solo los últimos 10 pedidos
  const displayedItems = items.slice(0, 10);
  
  // Verificar si hay pedidos para mostrar
  const hasOrders = items && items.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('eye')}
        {t('statement_details')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('statement_information')}
          </Typography>
          {/* Información General */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('client')}:
                </Typography>
                <Typography variant="body1">
                  {statement.clientName}
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('status')}:
                </Typography>
                <Chip
                  label={getCompanyStatusText(statement.status)}
                  color={getCompanyStatusColor(statement.status)}
                  size="small"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('due_date')}:
                </Typography>
                <Typography variant="body1">
                  {formatDate(dayjs(statement.dueDate))}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('last_payment')}:
                </Typography>
                <Typography variant="body1">
                  {statement.lastPayment ? formatDate(dayjs(statement.lastPayment)) : t('no_payments')}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Resumen Financiero */}
          <Typography variant="h6" gutterBottom>
            {t('financial_summary')}
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {t('total_amount')}
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(statement.totalAmount)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {t('paid_amount')}
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(statement.paidAmount)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {t('pending_amount')}
                </Typography>
                <Typography variant="h6" color="error.main">
                  {formatCurrency(statement.pendingAmount)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Items del Estado de Cuenta */}
          <Typography variant="h6" gutterBottom>
            {t('statement_items')}
          </Typography>
          
          {hasOrders ? (
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('description')}</TableCell>
                    <TableCell align="center">{t('date')}</TableCell>
                    <TableCell align="center">{t('due_date')}</TableCell>
                    <TableCell align="center">{t('amount')}</TableCell>
                    <TableCell align="center">{t('payment_status')}</TableCell>
                    <TableCell align="center">{t('payments')}</TableCell>
                    <TableCell align="center">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.orderNumber || `Orden #${item.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(dayjs(item.orderDate))}
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(dayjs(item.dueDate || item.orderDate))}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={B2BUtilsService.getBillingOrderStatusText(item.paymentStatus || item.status, item.dueDate, t)}
                          color={B2BUtilsService.getBillingOrderStatusColor(item.paymentStatus || item.status, item.dueDate)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ minWidth: 200 }}>
                          {item.payments && item.payments.length > 0 ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {formatCurrency(getTotalPaidForItem(item.payments))} / {formatCurrency(item.amount)}
                              </Typography>
                              {item.payments.map((payment, index) => (
                                <Box key={payment.id} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption" color="textSecondary">
                                    {formatDate(dayjs(payment.date))} - {formatCurrency(payment.amount)} ({getPaymentMethodText(payment.method)})
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {formatCurrency(item.paidAmount || 0)} / {formatCurrency(item.amount)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {t('no_payments')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          onClick={() => handleModifyBalance(item)}
                          sx={{ borderRadius: '50%' }}
                          title={t('modify_balance')}
                          disabled={item.paymentStatus === 'PAID' || item.status === 'PAID'}
                        >
                          {getIcon('money')}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {getIcon('receipt', { sx: { fontSize: 48, color: 'text.secondary' } })}
                <Typography variant="h6" color="text.secondary">
                  {t('no_orders_found')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('no_orders_description')}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Botón Ver Todo - Solo mostrar si hay pedidos */}
          {hasOrders && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  navigate(`/b2b/billing-statements/${statement.clientId}/billing-statements`, { 
                    state: { 
                      clientData: {
                        clientName: statement.clientName,
                        clientId: statement.clientId,
                        orders: statement.orders
                      }
                    } 
                  });
                  onClose();
                }}
                startIcon={getIcon('eye')}
              >
                {t('view_all_orders')}
              </Button>
            </Box>
          )}

          {/* Total */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('total')}: {formatCurrency(statement.totalAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('close')}
        </Button>
      </DialogActions>
      
      <EditPaymentStatusModal
        open={isModifyBalanceModalOpen}
        onClose={() => setIsModifyBalanceModalOpen(false)}
        onSave={handleUpdateBalance}
        statement={selectedItem ? {
          id: selectedItem.id,
          statementNumber: selectedItem.orderNumber,
          clientName: statement.clientName,
          totalAmount: selectedItem.amount,
          paidAmount: selectedItem.paidAmount || 0,
          pendingAmount: selectedItem.amount - (selectedItem.paidAmount || 0),
          status: selectedItem.status
        } : null}
      />
    </Dialog>
  );
};

export default ViewBillingStatementModal;
